import { useCallback, useEffect, useState } from 'react';
import { GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { dataGridCol, gridBooleanCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SaveButton } from '@shared/components/SaveButton';
import { ASSET_TYPE_OPTIONS } from '@shared/constants/lists';
import { RelativeWrapper } from '@shared/styledComponents';
import { IOption } from '@shared/interfaces';
import { LocalTabContent } from '@styles/CommonStyles';
import { IAssetTypeDataGrid, TAssetType } from '../../interfaces';
import { PhoenixGridHeader } from '../../shared/PhoenixGridHeader';
import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { requestUpdateAssets } from './queries';

const columns: GridColDef[] = [
  dataGridCol('assetType', {
    headerName: 'Asset Type Name',
    minWidth: 180,
    flex: 1,
  }),
  dataGridCol('isAllowed', {
    ...gridBooleanCell,
    flex: 1,
    editable: false,
  }),
];

const getList = (list: IOption<string | number>[], data?: TAssetType[]) =>
  list
    .map((item) => ({
      id: item.value,
      assetType: item.label,
      isAllowed: data?.find((obj) => obj.assetType === item.value)?.isAllowed || false,
    }))
    ?.sort((assetTypeOne, assetTypeTwo) =>
      assetTypeOne.isAllowed === assetTypeTwo.isAllowed ? 0 : assetTypeOne.isAllowed ? -1 : 1
    ) || [];

export const Assets = ({ data, lenderId }: ILenderUtilityTabProps) => {
  const [updating, setUpdating] = useState(false);
  const [assetTypeList, setAssetTypeList] = useState<IAssetTypeDataGrid[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

  useEffect(() => {
    if (data?.assetTypes) {
      const assetTList = [...data.assetTypes];
      const initialSelectedRows = assetTList.filter((row) => row.isAllowed).map((row) => row.assetType);
      setSelectionModel(initialSelectedRows);
      const listItems = getList(ASSET_TYPE_OPTIONS, assetTList);
      setAssetTypeList(listItems);
    }
  }, [data?.assetTypes]);

  const onSubmit = useCallback(async () => {
    setUpdating(true);
    const selectedIds = new Set(selectionModel);
    const updatedTypes = assetTypeList?.map((row) => ({
      assetType: row.assetType?.replace(' ', ''),
      isAllowed: selectedIds.has(row.id),
    }));
    
    await requestUpdateAssets(lenderId, updatedTypes);
    setUpdating(false);
  }, [selectionModel, assetTypeList]);

  const onRowSelection = useCallback(
    (defaultRows: GridSelectionModel) => {
      setSelectionModel(defaultRows);
    },
    []
  );

  return (
    <RelativeWrapper>
      <SaveButton
        disabled={updating}
        isLoading={updating}
        onClick={onSubmit}
      />

      <LocalTabContent>
        <PhoenixGridHeader
          title="Assets"
          subtitle="Check all assets that are allowed by lender."
          justify="flex-start"
        />

        <PhoenixGrid
          autoHeight
          checkboxSelection
          name="Asset"
          gridId={`Asset-${lenderId}`}
          pagination={false}
          columns={columns}
          loading={!assetTypeList}
          selectionModel={selectionModel}
          rows={assetTypeList}
          getRowId={(row) => row.id}
          onSelectionModelChange={onRowSelection}
        />
      </LocalTabContent>
    </RelativeWrapper>
  );
};
